import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { addToast } from '../../store/toast/toastSlice'
import { setFolderView, setIsInitialLoading, setServiceId } from '../../store/documentFileBrowser/documentFileBrowserSlice'
import { type IntegrationServiceId } from '../../common/integrations'
import {
  useLazyListDocumentConnectorItemsQuery,
  useLazySearchDocumentConnectorItemsQuery,
  useListConnectorWorkspacesQuery,
  type DocumentConnectorItem,
  type WorkspaceNode,
} from '../../store/documentConnectors/documentConnectors'
import { type Breadcrumb, type FileBrowserItem, type FolderContents, type FolderItemsPage, ROOT_ANCHOR_ID } from './types'

const itemExternalId = (item: DocumentConnectorItem): string => item.integration?.externalId ?? item.id ?? ''

const mapDocumentConnectorItemToFileBrowserItem = (item: DocumentConnectorItem): FileBrowserItem => ({
  id: itemExternalId(item),
  name: item.name,
  type: item.type === 'folder' ? 'folder' : 'file',
  size: item.size ?? undefined,
  modifiedAt: item.modifiedAt ?? undefined,
  modifiedBy: item.modifiedBy?.name ?? undefined,
  itemCount: item.itemCount ?? undefined,
  integration: item.integration ?? undefined,
})

const firstSelectableId = (nodes: WorkspaceNode[]): string | null =>
  nodes.reduce<string | null>((found, node) => found ?? (node.selectable ? node.id : firstSelectableId(node.children ?? [])), null)

interface UseDocumentFileBrowserOptions {
  initialFolderIds?: string[]
  initialFoldersReady?: boolean
}

export const useDocumentFileBrowser = (serviceId: IntegrationServiceId, options?: UseDocumentFileBrowserOptions) => {
  const initialFoldersReady = options?.initialFoldersReady ?? true
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const [fetchItems] = useLazyListDocumentConnectorItemsQuery()
  const [fetchSearchItems] = useLazySearchDocumentConnectorItemsQuery()

  const {
    data: workspaces = [],
    isFetching: isFetchingWorkspaces,
    isLoading: isWorkspacesLoading,
  } = useListConnectorWorkspacesQuery({ accountId, connector: serviceId }, { skip: !accountId })
  const hasWorkspaces = workspaces.length > 0

  const [selectedRoot, setSelectedRoot] = useState<string | null>(null)
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(null)

  const initialFolderIdsKey = options?.initialFolderIds?.join(' ') ?? ''
  const initialFolderIds = useMemo(() => (initialFolderIdsKey ? initialFolderIdsKey.split(' ') : undefined), [initialFolderIdsKey])

  const defaultRoot = useMemo(
    () => (hasWorkspaces && !initialFolderIds?.length ? firstSelectableId(workspaces) : null),
    [hasWorkspaces, initialFolderIds, workspaces],
  )
  const effectiveRoot = selectedRoot ?? defaultRoot
  const pickerWorkspace = selectedRoot ?? currentWorkspaceId ?? defaultRoot

  const seedFolderIds = useMemo(() => (effectiveRoot ? [effectiveRoot] : (initialFolderIds ?? undefined)), [effectiveRoot, initialFolderIds])

  const rootFolderIds = useMemo(
    () => (pickerWorkspace ? [pickerWorkspace] : initialFolderIds && initialFolderIds.length > 1 ? initialFolderIds : undefined),
    [pickerWorkspace, initialFolderIds],
  )

  const seedKey = seedFolderIds?.join(' ') ?? ''
  const isMultiFolder = (seedFolderIds?.length ?? 0) > 1
  const readyToLoad = !isFetchingWorkspaces && (!hasWorkspaces || seedFolderIds != null)

  const fetchFolder = useCallback(
    async (folderIds: string[] | undefined): Promise<FolderItemsPage & { folder: DocumentConnectorItem | null }> => {
      const result = await fetchItems({ accountId, connector: serviceId, folderIds }).unwrap()
      const entries = Object.values(result)
      if (entries.length > 1) {
        return { items: entries.map((entry) => mapDocumentConnectorItemToFileBrowserItem(entry.folder)), folder: null }
      }
      return {
        items: (entries[0]?.items ?? []).map(mapDocumentConnectorItemToFileBrowserItem),
        folder: entries[0]?.folder ?? null,
        totalCount: entries[0]?.totalCount ?? null,
        nextCursor: entries[0]?.nextCursor ?? null,
      }
    },
    [accountId, serviceId, fetchItems],
  )

  const deriveBreadcrumbs = useCallback((folder: DocumentConnectorItem | null): Breadcrumb[] => {
    if (!folder) {
      return [{ id: ROOT_ANCHOR_ID, name: '' }]
    }
    return [
      { id: ROOT_ANCHOR_ID, name: '' },
      ...(folder.ancestorFolders ?? []).map((ancestor) => ({ id: ancestor.id, name: ancestor.name ?? '' })),
      { id: itemExternalId(folder), name: folder.name, integration: folder.integration ?? undefined },
    ]
  }, [])

  useEffect(() => {
    dispatch(setServiceId(serviceId))
    dispatch(setIsInitialLoading(true))
  }, [serviceId, dispatch])

  useEffect(() => {
    if (!readyToLoad || !initialFoldersReady) {
      return
    }
    const isDeepEntry = !effectiveRoot && initialFolderIds?.length === 1
    dispatch(setIsInitialLoading(true))
    fetchFolder(seedFolderIds)
      .then(({ items, folder, totalCount, nextCursor }) => {
        setCurrentWorkspaceId(folder?.integration?.externalWorkspaceId ?? null)
        dispatch(setFolderView({ items, totalCount, nextCursor, breadcrumbs: deriveBreadcrumbs(isDeepEntry ? folder : null) }))
      })
      .catch(() => {
        dispatch(addToast({ variant: 'error', title: 'Failed to load files', messages: ['Could not connect to the file service. Please try again.'] }))
      })
      .finally(() => dispatch(setIsInitialLoading(false)))
  }, [readyToLoad, seedKey, dispatch, fetchFolder, deriveBreadcrumbs, seedFolderIds, effectiveRoot, initialFolderIds, initialFoldersReady])

  const loadFolder = useCallback(
    async (folderId: string | null): Promise<FolderContents> => {
      if (!folderId && isMultiFolder) {
        const { items } = await fetchFolder(rootFolderIds)
        return { items, breadcrumbs: deriveBreadcrumbs(null) }
      }
      const { items, folder, totalCount, nextCursor } = await fetchFolder(folderId ? [folderId] : rootFolderIds)
      return { items, totalCount, nextCursor, breadcrumbs: deriveBreadcrumbs(folderId ? folder : null) }
    },
    [fetchFolder, deriveBreadcrumbs, rootFolderIds, isMultiFolder],
  )

  const loadMoreItems = useCallback(
    async (folderId: string | null, cursor: string): Promise<FolderItemsPage> => {
      const folderIds = folderId ? [folderId] : rootFolderIds
      const result = await fetchItems({ accountId, connector: serviceId, folderIds, cursor }).unwrap()
      const entry = Object.values(result)[0]
      return {
        items: (entry?.items ?? []).map(mapDocumentConnectorItemToFileBrowserItem),
        totalCount: entry?.totalCount ?? null,
        nextCursor: entry?.nextCursor ?? null,
      }
    },
    [accountId, serviceId, fetchItems, rootFolderIds],
  )

  const fetchSearchPage = useCallback(
    async (query: string, currentFolderId: string | null, cursor?: string): Promise<FolderItemsPage> => {
      const ancestorFolderIds = currentFolderId ? [currentFolderId] : (rootFolderIds ?? undefined)
      const result = await fetchSearchItems({ accountId, connector: serviceId, query, ancestorFolderIds, cursor }).unwrap()
      return {
        items: (result?.items ?? []).map(mapDocumentConnectorItemToFileBrowserItem),
        totalCount: result?.totalCount ?? null,
        nextCursor: result?.nextCursor ?? null,
      }
    },
    [accountId, serviceId, fetchSearchItems, rootFolderIds],
  )

  const searchItems = useCallback(
    (query: string, currentFolderId: string | null): Promise<FolderItemsPage> => fetchSearchPage(query, currentFolderId),
    [fetchSearchPage],
  )

  const loadMoreSearchItems = useCallback(
    (query: string, currentFolderId: string | null, cursor: string): Promise<FolderItemsPage> => fetchSearchPage(query, currentFolderId, cursor),
    [fetchSearchPage],
  )

  const onWorkspaceChange = useCallback((nodeId: string) => setSelectedRoot(nodeId), [])

  return {
    loadFolder,
    loadMoreItems,
    searchItems,
    loadMoreSearchItems,
    workspaces,
    workspace: pickerWorkspace,
    onWorkspaceChange,
    isWorkspacesLoading,
  }
}
